import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  AI_CONTENT_REPORT_LIMITS,
  type AiContentReportReason,
  type AiContentReportSurface,
} from '@orborix/shared-types';
import { useSheetDrag } from '../../hooks/useSheetDrag.js';
import { AiDataApiError, reportAiContent } from '../../lib/aiDataApi.js';
import { AI_REPORT_COPY } from './aiReportCopy.js';
import { track } from '../../lib/analytics.js';
import './aiReport.css';

/**
 * "Report this reply" on AI-generated text (Google Play AI-Generated Content
 * policy: in-app flagging without leaving the app; Apple 1.2). A flag icon
 * button that opens a bottom sheet — reason (offensive / incorrect / other) +
 * optional comment → `POST /api/modules/english/ai-content-reports` → a short
 * confirmation toast. The reply text goes as the person saw it.
 *
 * The sheet and the toast are portaled to `<body>`: the coach bubble can carry
 * `content-visibility` (paint containment), which would clip a fixed-position
 * descendant.
 */
export interface ReportAiReplyProps {
  surface: AiContentReportSurface;
  /** The reply as shown (markdown already stripped). */
  text: string;
  sessionId?: string | undefined;
  /** Voice session turn of the reply, when the surface has one. */
  turnIndex?: number | undefined;
  /** Extra class for the trigger (each surface sizes its action row). */
  className?: string;
}

const copy = AI_REPORT_COPY.es;
const TOAST_MS = 3200;

function FlagIcon({ size = 12 }: { size?: number }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M5 21V4" />
      <path d="M5 4h11l-2 4 2 4H5" />
    </svg>
  );
}

export function ReportAiReply({
  surface,
  text,
  sessionId,
  turnIndex,
  className,
}: ReportAiReplyProps) {
  const [open, setOpen] = useState(false);
  const [toast, setToast] = useState(false);

  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(false), TOAST_MS);
    return () => window.clearTimeout(t);
  }, [toast]);

  return (
    <>
      <button
        type="button"
        className={`bubble-icon-btn ai-report-btn ${className ?? ''}`}
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        aria-label={copy.buttonLabel}
        title={copy.buttonLabel}
        aria-haspopup="dialog"
      >
        <FlagIcon />
      </button>
      {open &&
        createPortal(
          <ReportSheet
            surface={surface}
            text={text}
            sessionId={sessionId}
            turnIndex={turnIndex}
            onClose={() => setOpen(false)}
            onSent={() => {
              setOpen(false);
              setToast(true);
            }}
          />,
          document.body,
        )}
      {toast &&
        createPortal(
          <div className="ai-report-toast" role="status">
            {copy.sent}
          </div>,
          document.body,
        )}
    </>
  );
}

interface ReportSheetProps {
  surface: AiContentReportSurface;
  text: string;
  sessionId: string | undefined;
  turnIndex: number | undefined;
  onClose: () => void;
  onSent: () => void;
}

export function ReportSheet({
  surface,
  text,
  sessionId,
  turnIndex,
  onClose,
  onSent,
}: ReportSheetProps) {
  const { grabProps, sheetStyle } = useSheetDrag(onClose);
  const [reason, setReason] = useState<AiContentReportReason | null>(null);
  const [comment, setComment] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const submit = async (): Promise<void> => {
    if (!reason) {
      setError(copy.pickReason);
      return;
    }
    setSending(true);
    setError(null);
    const trimmed = comment.trim();
    try {
      await reportAiContent({
        surface,
        reason,
        text: text.slice(0, AI_CONTENT_REPORT_LIMITS.text),
        ...(trimmed ? { comment: trimmed } : {}),
        // The turn only makes sense with its session.
        ...(sessionId ? { sessionId, ...(turnIndex != null ? { turnIndex } : {}) } : {}),
      });
      // The reason and the surface only: the reported text and the comment stay out.
      track('ai_content_reported', { reason, surface });
      onSent();
    } catch (err: unknown) {
      console.warn('[ai-report] submit failed', err);
      setError(
        err instanceof AiDataApiError && err.code === 'rate_limited'
          ? copy.rateLimited
          : copy.error,
      );
      setSending(false);
    }
  };

  return (
    // Stops the clicks here: React events bubble through the portal to the
    // bubble that rendered it (word taps, thread gestures).
    <div
      className="ai-report-overlay"
      onClick={(e) => {
        e.stopPropagation();
        onClose();
      }}
    >
      <div
        className="ai-report-sheet"
        style={sheetStyle}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="ai-report-title"
      >
        <div className="ai-report-handle sheet-grab" {...grabProps} />
        <h2 id="ai-report-title" className="ai-report-title">
          {copy.title}
        </h2>
        <p className="ai-report-sub">{copy.subtitle}</p>
        <blockquote className="ai-report-quote">{text}</blockquote>

        <div className="ai-report-reasons" role="radiogroup" aria-label={copy.title}>
          {copy.reasons.map((r) => (
            <button
              key={r.id}
              type="button"
              role="radio"
              aria-checked={reason === r.id}
              className={`ai-report-reason${reason === r.id ? ' is-on' : ''}`}
              disabled={sending}
              onClick={() => {
                setReason(r.id);
                setError(null);
              }}
            >
              {r.label}
            </button>
          ))}
        </div>

        <label className="ai-report-label" htmlFor="ai-report-comment">
          {copy.commentLabel}
        </label>
        <textarea
          id="ai-report-comment"
          className="ai-report-comment"
          value={comment}
          maxLength={AI_CONTENT_REPORT_LIMITS.comment}
          placeholder={copy.commentPlaceholder}
          rows={3}
          disabled={sending}
          onChange={(e) => setComment(e.target.value)}
        />

        {error && (
          <p className="ai-report-error" role="alert">
            {error}
          </p>
        )}

        <div className="ai-report-actions">
          <button type="button" className="ai-report-cancel" onClick={onClose} disabled={sending}>
            {copy.cancel}
          </button>
          <button
            type="button"
            className="ai-report-submit"
            onClick={() => void submit()}
            disabled={sending}
          >
            {sending ? copy.sending : copy.submit}
          </button>
        </div>
      </div>
    </div>
  );
}
